// @ts-nocheck

import React from 'react';
import { ExpandMore, ChevronRight, Folder, FolderOpen, InsertDriveFile } from '@mui/icons-material';
import { Box, Typography, Tooltip, CircularProgress } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import type { SimpleTreeViewProps } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import type { TreeItemProps } from '@mui/x-tree-view/TreeItem';
import styles from '@/components/atoms/TreeView/treeview.module.css';

export interface TreeViewNode {
  id: string;
  label: string;
  children?: TreeViewNode[];
  hasChildren?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactElement;
  expandedIcon?: React.ReactElement;
  collapsedIcon?: React.ReactElement;
  endIcon?: React.ReactElement;
  tooltip?: string;
  className?: string;
  nodeData?: any; // Additional data attached to the node
  [key: string]: any;
}

interface TreeViewProps extends Omit<SimpleTreeViewProps, 'children'> {
  nodes: TreeViewNode[];
  loading?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'compact' | 'dense';
  showIcons?: boolean;
  showEndIcons?: boolean;
  customIcons?: {
    default?: React.ReactElement;
    expanded?: React.ReactElement;
    collapsed?: React.ReactElement;
    expandMore?: React.ReactElement;
    chevronRight?: React.ReactElement;
    folder?: React.ReactElement;
    folderOpen?: React.ReactElement;
    file?: React.ReactElement;
  };
  onNodeClick?: (event: React.SyntheticEvent, nodeId: string, node: TreeViewNode) => void;
  onNodeToggle?: (event: React.SyntheticEvent, nodeIds: string[]) => void;
  onNodeSelect?: (event: React.SyntheticEvent, nodeId: string | null) => void;
  onNodeFocus?: (event: React.SyntheticEvent, nodeId: string | null) => void;
  renderNode?: (node: TreeViewNode) => React.ReactNode;
  renderEndIcon?: (node: TreeViewNode) => React.ReactElement;
  renderLabel?: (node: TreeViewNode) => React.ReactNode;
  loadingComponent?: React.ReactElement;
  emptyComponent?: React.ReactElement;
  className?: string;
  treeItemProps?: Partial<TreeItemProps>;
  maxHeight?: string | number;
  enableVirtualization?: boolean;
  searchTerm?: string;
  highlightSearchTerm?: boolean;
  expandOnSearch?: boolean;
  expandedItems?: string[];
  selectedItems?: string | string[];
  defaultExpandedItems?: string[];
  defaultSelectedItems?: string | string[];
  showCustomExpandButton?: boolean;
  customExpandElement?: (
    node: TreeViewNode,
    isExpanded: boolean,
    onToggle: () => void
  ) => React.ReactElement;
  showExpandFooter?: boolean;
  customExpandFooter?: (
    node: TreeViewNode,
    onAddChild: (nodeId: string, nodeData: any) => void
  ) => React.ReactElement;
  onAddChild?: (nodeId: string, nodeData: any) => void;
  onNodeExpand?: (nodeId: string, nodeData: any) => void;
}

const AtomTreeView: React.FC<TreeViewProps> = ({
  nodes = [],
  loading = false,
  size = 'medium',
  variant = 'default',
  showIcons = true,
  showEndIcons = false,
  customIcons,
  onNodeClick,
  onNodeToggle,
  onNodeSelect,
  onNodeFocus,
  renderNode,
  renderEndIcon,
  renderLabel,
  loadingComponent,
  emptyComponent,
  className = '',
  treeItemProps,
  maxHeight = '400px',
  searchTerm = '',
  highlightSearchTerm = false,
  expandOnSearch = false,
  collapseIcon,
  expandIcon,
  showCustomExpandButton = false,
  customExpandElement,
  showExpandFooter = false,
  customExpandFooter,
  onAddChild,
  onNodeExpand,
  ...treeViewProps
}) => {
  // Default icons
  const defaultIcons = {
    expandMore: <ExpandMore />,
    chevronRight: <ChevronRight />,
    folder: <Folder />,
    folderOpen: <FolderOpen />,
    file: <InsertDriveFile />,
    ...customIcons,
  };

  const [localExpandedItems, setLocalExpandedItems] = React.useState<string[]>(
    treeViewProps.defaultExpandedItems || []
  );

  // Search functionality
  const filterNodes = (nodes: TreeViewNode[], term: string): TreeViewNode[] => {
    if (!term) return nodes;

    return nodes.reduce((filtered: TreeViewNode[], node) => {
      const matchesSearch = node.label.toLowerCase().includes(term.toLowerCase());
      const filteredChildren = node.children ? filterNodes(node.children, term) : [];

      if (matchesSearch || filteredChildren.length > 0) {
        filtered.push({
          ...node,
          children: filteredChildren.length > 0 ? filteredChildren : node.children,
        });
      }

      return filtered;
    }, []);
  };

  // Highlight search term in text
  const highlightText = (text: string, term: string) => {
    if (!term || !highlightSearchTerm) return text;

    const regex = new RegExp(`(${term})`, 'gi');
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} className={styles.highlighted}>
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  // Get expanded nodes for search
  const getExpandedNodesForSearch = (nodes: TreeViewNode[], term: string): string[] => {
    if (!term || !expandOnSearch) return [];

    const expandedIds: string[] = [];

    const traverse = (nodeList: TreeViewNode[]) => {
      nodeList.forEach(node => {
        if (node.children && node.children.length > 0) {
          const hasMatchingDescendant = (children: TreeViewNode[]): boolean => {
            return children.some(child => {
              if (child.label.toLowerCase().includes(term.toLowerCase())) {
                return true;
              }
              return child.children ? hasMatchingDescendant(child.children) : false;
            });
          };

          if (hasMatchingDescendant(node.children)) {
            expandedIds.push(node.id);
          }

          traverse(node.children);
        }
      });
    };

    traverse(nodes);
    return expandedIds;
  };

  // Render tree item content
  const renderTreeItemContent = (node: TreeViewNode) => {
    if (renderNode) {
      return renderNode(node);
    }

    const labelContent = renderLabel ? renderLabel(node) : highlightText(node.label, searchTerm);
    const isExpanded = localExpandedItems.includes(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const canExpand = hasChildren || node.hasChildren;

    return (
      <Box className={styles.nodeContent} style={{ display: 'flex', alignItems: 'center' }}>
        {/* Only show icons if showIcons is true */}
        {showIcons && (
          <Box className={styles.nodeIcon}>
            {node.loading ? (
              <CircularProgress size={16} className={styles.loadingIcon} />
            ) : node.icon ? (
              node.icon
            ) : node.children && node.children.length > 0 ? (
              defaultIcons.folder
            ) : (
              defaultIcons.file
            )}
          </Box>
        )}

        <Typography
          variant="body2"
          className={`${styles.nodeLabel} ${node.disabled ? styles.disabledLabel : ''}`}
          style={{ flex: 1 }}
        >
          {labelContent}
        </Typography>

        {/* Custom expand button - MOVED AFTER LABEL */}
        {showCustomExpandButton && canExpand && (
          <Box className={styles.customExpandButton} style={{ marginLeft: '8px' }}>
            {customExpandElement ? (
              customExpandElement(node, isExpanded, () => {
                const newExpanded = isExpanded
                  ? localExpandedItems.filter(id => id !== node.id)
                  : [...localExpandedItems, node.id];
                handleNodeToggle({} as React.SyntheticEvent, newExpanded);
              })
            ) : (
              <button
                onClick={e => {
                  e.stopPropagation();
                  const newExpanded = isExpanded
                    ? localExpandedItems.filter(id => id !== node.id)
                    : [...localExpandedItems, node.id];
                  handleNodeToggle({} as React.SyntheticEvent, newExpanded);
                }}
                style={{
                  background: 'none',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                +
              </button>
            )}
          </Box>
        )}

        {showEndIcons && (renderEndIcon ? renderEndIcon(node) : node.endIcon) && (
          <Box className={styles.endIcon}>{renderEndIcon ? renderEndIcon(node) : node.endIcon}</Box>
        )}
      </Box>
    );
  };

  // Render tree item
  const renderTreeItem = (node: TreeViewNode): React.ReactElement => {
    const content = renderTreeItemContent(node);
    const isExpanded = localExpandedItems.includes(node.id);

    const treeItem = (
      <TreeItem
        key={node.id}
        itemId={node.id}
        label={content}
        disabled={node.disabled}
        className={`${styles.treeItem} ${node.className || ''} ${
          variant === 'compact' ? styles.compact : ''
        } ${variant === 'dense' ? styles.dense : ''}`}
        {...treeItemProps}
      >
        {node.children && node.children.map(child => renderTreeItem(child))}

        {/* Add footer AFTER all children */}
        {showExpandFooter && isExpanded && (
          <Box
            style={{
              marginLeft: '24px',
              marginTop: '8px',
              marginBottom: '8px',
              paddingLeft: '16px',
            }}
          >
            {customExpandFooter ? (
              customExpandFooter(node, onAddChild || (() => {}))
            ) : (
              <button
                onClick={e => {
                  e.stopPropagation();
                  if (onAddChild) {
                    onAddChild(node.id, node.nodeData);
                  }
                }}
                style={{
                  background: 'none',
                  border: '1px dashed #093fc3',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  color: '#093fc3',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                + Add New
              </button>
            )}
          </Box>
        )}
      </TreeItem>
    );

    return node.tooltip ? (
      <Tooltip title={node.tooltip} key={node.id}>
        {treeItem}
      </Tooltip>
    ) : (
      treeItem
    );
  };

  // Handle node selection
  const handleNodeSelect = (event: React.SyntheticEvent, itemId: string | null) => {
    if (onNodeSelect && itemId) {
      onNodeSelect(event, itemId);
    }
  };

  // Handle node toggle
  const handleNodeToggle = (event: React.SyntheticEvent, itemIds: string[]) => {
    setLocalExpandedItems(itemIds);

    // Find newly expanded items and trigger onNodeExpand
    const previouslyExpanded = localExpandedItems;
    const newlyExpanded = itemIds.filter(id => !previouslyExpanded.includes(id));

    newlyExpanded.forEach(nodeId => {
      if (onNodeExpand) {
        const findNode = (nodes: TreeViewNode[], targetId: string): TreeViewNode | null => {
          for (const node of nodes) {
            if (node.id === targetId) return node;
            if (node.children) {
              const found = findNode(node.children, targetId);
              if (found) return found;
            }
          }
          return null;
        };

        const node = findNode(nodes, nodeId);
        if (node) {
          onNodeExpand(nodeId, node.nodeData);
        }
      }
    });

    if (onNodeToggle) {
      onNodeToggle(event, itemIds);
    }
  };

  // Handle node focus
  const handleNodeFocus = (event: React.SyntheticEvent, itemId: string | null) => {
    if (onNodeFocus && itemId) {
      onNodeFocus(event, itemId);
    }
  };

  // Filter nodes based on search
  const filteredNodes = searchTerm ? filterNodes(nodes, searchTerm) : nodes;

  // Get auto-expanded nodes for search
  const searchExpandedNodes = getExpandedNodesForSearch(nodes, searchTerm);

  // Loading state
  if (loading) {
    return (
      <Box className={`${styles.treeViewWrapper} ${className}`}>
        {loadingComponent || (
          <Box className={styles.loadingContainer}>
            <CircularProgress size={24} />
            <Typography variant="body2" className={styles.loadingText}>
              Loading...
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Empty state
  if (filteredNodes.length === 0) {
    return (
      <Box className={`${styles.treeViewWrapper} ${className}`}>
        {emptyComponent || (
          <Box className={styles.emptyContainer}>
            <Typography variant="body2" className={styles.emptyText}>
              {searchTerm ? 'No matching items found' : 'No items to display'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box className={`${styles.treeViewWrapper} ${className}`} style={{ maxHeight }}>
      <SimpleTreeView
        {...treeViewProps}
        defaultExpandedItems={[
          ...searchExpandedNodes,
          ...(treeViewProps.defaultExpandedItems || []),
        ]}
        onSelectedItemsChange={handleNodeSelect}
        onExpandedItemsChange={handleNodeToggle}
        onItemFocus={handleNodeFocus}
        className={`${styles.treeView} ${size === 'small' ? styles.small : ''} ${
          size === 'large' ? styles.large : ''
        }`}
      >
        {filteredNodes.map(node => renderTreeItem(node))}
      </SimpleTreeView>
    </Box>
  );
};

export default AtomTreeView;
